/**
 * The Area page's words (slice 6d spec §8.2), kept out of the components so
 * they're tested on their own.
 */
import type { Me } from "../../app/me";
import type { Area, AreaEdit, AreaPage } from "./api";

type Person = { id: string; name: string };

/**
 * Who wrote Where it stands, from its `summary_by`: an agent's name ("the
 * crew" for one not in the workspace), "you", a person's first name, or
 * "someone" while the people haven't loaded. Null when nobody has written it.
 */
export function authorName(by: string | null, me: Me, people: readonly Person[] = []) {
  if (!by) return null;
  const [kind, id] = by.split(":");
  if (kind === "agent") return me.agents.find((a) => a.id === id)?.name ?? "the crew";
  if (id === me.user.id) return "you";
  const name = people.find((p) => p.id === id)?.name;
  return name ? name.split(/\s+/)[0] || name : "someone";
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** "This week: 11 replies and about 18 minutes of computer time." */
export function weekLine(week: NonNullable<AreaPage["week"]>): string {
  const minutes = Math.round(week.computer_ms / 60_000);
  const time =
    week.computer_ms === 0
      ? "no"
      : minutes < 1
        ? "under a minute of"
        : `about ${plural(minutes, "minute", "minutes")} of`;
  return `This week: ${plural(week.replies, "reply", "replies")} and ${time} computer time.`;
}

/** The edit's changes from the Area as it is: only what differs, words trimmed. */
export function areaChanges(
  area: Pick<Area, "label" | "icon" | "summary">,
  draft: { label: string; icon: string; summary: string },
): AreaEdit {
  const label = draft.label.trim();
  const summary = draft.summary.trim();
  return {
    ...(label !== area.label ? { label } : {}),
    ...(draft.icon !== area.icon ? { icon: draft.icon } : {}),
    ...(summary !== area.summary ? { summary } : {}),
  };
}

/** Where the Area sits in the sidebar, and whether Move up and Move down can go. */
export function areaMoves(area: Pick<Area, "id" | "archived_at">, me: Pick<Me, "areas">) {
  const index = Math.max(
    0,
    me.areas.findIndex((a) => a.id === area.id),
  );
  const live = area.archived_at === null;
  return { index, up: live && index > 0, down: live && index < me.areas.length - 1 };
}

type Filed = { stage: string; area_id?: string | null; area?: { id: string } | null };

/** A row's Area id: `area_id` where the API sends it, else its chip's. */
const areaIdOf = (t: Filed) => (t.area_id !== undefined ? t.area_id : (t.area?.id ?? null));

/**
 * How many of a chat's open tasks move with it (spec §4.2): those in its
 * Area now (with none, those with none). Done and canceled ones stay.
 */
export function openTasksMoving(tasks: readonly Filed[], areaId: string | null): number {
  return tasks.filter((t) => t.stage !== "done" && t.stage !== "canceled" && areaIdOf(t) === areaId)
    .length;
}

/** The Area sheet's line (spec §8.3): "Its 2 open tasks move too.", or null with none. */
export function movesLine(n: number): string | null {
  if (n === 0) return null;
  return n === 1 ? "Its open task moves too." : `Its ${n} open tasks move too.`;
}

/** A list's `?area=` read back: a slug, `none`, or null (all areas). */
export function areaParam(raw: string | null): string | null {
  const v = raw?.trim().toLowerCase() ?? "";
  return /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(v) ? v : null;
}

/** The id New task, New doc and New routine file into from a list filtered to a live Area. */
export function filingArea(area: string | null, me: Pick<Me, "areas">): string | undefined {
  if (!area || area === "none") return undefined;
  return me.areas.find((a) => a.slug === area)?.id;
}

/** The crew's "Made the area …" line's Area slug (spec §7.2): its Move chats in link. */
export function lineArea(message: { kind: string; data: Record<string, unknown> }): string | null {
  const slug = message.data.slug;
  return message.kind === "system" &&
    message.data.event === "area_created" &&
    typeof slug === "string"
    ? slug
    : null;
}

const count = (n: number, one: string) => `${n} ${one}${n === 1 ? "" : "s"}`;

/** Move these in?'s button: "Move 4 chats", "Move 1 doc", "Move 2 chats and 1 doc". */
export function moveLabel(chats: number, docs: number): string {
  const parts = [chats ? count(chats, "chat") : "", docs ? count(docs, "doc") : ""];
  const what = parts.filter(Boolean).join(" and ");
  return what ? `Move ${what}` : "Move";
}

/** One row Move these in? lists: a chat or a doc, with when it last changed. */
export type MoveInRow = { id: string; title: string; at: string };

/**
 * Move these in?'s lists (slice 6d PR 9): the chats, then the docs, those
 * with rows only, each headed "Chats" or "Docs" when both have rows.
 */
export function moveInSections(data: {
  suggestions: { id: string; title: string; last_message_at: string }[];
  docs?: { id: string; title: string; updated_at: string }[];
}): { kind: "chat" | "doc"; heading: string | null; rows: MoveInRow[] }[] {
  const all = [
    {
      kind: "chat" as const,
      label: "Chats",
      rows: data.suggestions.map((c) => ({ id: c.id, title: c.title, at: c.last_message_at })),
    },
    {
      kind: "doc" as const,
      label: "Docs",
      rows: (data.docs ?? []).map((d) => ({ id: d.id, title: d.title, at: d.updated_at })),
    },
  ].filter((s) => s.rows.length > 0);
  return all.map((s) => ({
    kind: s.kind,
    heading: all.length > 1 ? s.label : null,
    rows: s.rows,
  }));
}

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

/** "Marcus", "Marcus and Sam", "Marcus, Sam and 2 others": the crew's other people, by first name. */
function namesOf(others: readonly Person[]): string {
  const names = others.map((p) => firstName(p.name));
  if (names.length <= 2) return names.join(" and ");
  return `${names.slice(0, 2).join(", ")} and ${plural(names.length - 2, "other", "others")}`;
}

/**
 * New area's Everyone line (slice 6g §8.1): who else sees a public Area.
 * "People you invite…" in a workspace of one.
 */
export function everyoneLine(others: readonly Person[]): string {
  if (others.length === 0) return "People you invite see its chats, tasks and docs too.";
  const verb = others.length === 1 ? "sees" : "see";
  return `${namesOf(others)} ${verb} its chats, tasks and docs too.`;
}

/** The private Area page's line (§8.3): "Marcus doesn't see this area." */
export function unseenLine(others: readonly Person[]): string {
  if (others.length === 0) return "No one else sees this area.";
  return `${namesOf(others)} ${others.length === 1 ? "doesn't" : "don't"} see this area.`;
}

/** The private Area page's side note (§8.3), under "Only you see this area". */
export function privateNote(others: readonly Person[]): string {
  const who =
    others.length === 0
      ? "No one else sees it"
      : `${namesOf(others)} ${others.length === 1 ? "doesn't" : "don't"} see it`;
  return `${who}, its chats, tasks or docs. The crew uses what's here only in this area's chats, and never brings it up anywhere else.`;
}
